import { create } from "zustand";
import { devtools } from "zustand/middleware";
import {
  DevelopmentState,
  AddGoalProps,
  EditGoalProps,
  LearningPlan,
  GoalTimestamp,
} from "../types/api";
import {
  addGoalToCalendar,
  removeGoalFromCaledar,
  editCalendarGoal,
  getLearningPlanGoals,
  getLearningPlan,
  addLearningPlan,
  editLearningPlan,
  deleteLearningPlan,
  addTaskToGoal,
  editTaskInGoal,
  deleteTaskInGoal,
} from "../api/developmentApi";

const useDevelopmentStore = create<DevelopmentState>()(
  devtools(
    (set) => ({
      learningPlan: [],
      goals: [],
      currentPlanId: null,
      loading: false,
      error: null,

      fetchDevelopmentPlan: async () => {
        set({ loading: true, error: null });
        try {
          const response = await getLearningPlan();
          if (!response) {
            throw new Error("Failed to fetch goals");
          }
          set((state) => ({
            learningPlan: response.data,
            currentPlanId: state.currentPlanId ?? response.data[0].id,
            loading: false,
          }));
          return response.data;
        } catch (error: any) {
          set({ error: error.message, loading: false });
        }
      },
      addLearningPlan: async (credentials: LearningPlan) => {
        set({ loading: true, error: null });
        try {
          const response = await addLearningPlan(credentials);

          set((state) => ({
            learningPlan: state.learningPlan
              ? [...state.learningPlan, response.data]
              : [response.data],
            loading: false,
          }));
          return response.data;
        } catch (error: any) {
          set({ error: error.message, loading: false });
          throw error;
        }
      },
      editLearningPlan: async (credentials: LearningPlan) => {
        set({ loading: true, error: null });
        try {
          const response = await editLearningPlan(credentials);

          set((state) => ({
            learningPlan: state.learningPlan
              ? state.learningPlan.map((plan) =>
                  plan.id === response.data.id ? response.data : plan
                )
              : [response.data],
            loading: false,
          }));

          return response;
        } catch (error: any) {
          set({ error: error.message, loading: false });
          throw error;
        }
      },

      removeLearningPlan: async (id: number) => {
        set({ loading: true, error: null });
        try {
          await deleteLearningPlan(id);

          set((state) => ({
            learningPlan: state.learningPlan
              ? state.learningPlan.filter((plan) => plan.id !== id)
              : [],
            loading: false,
          }));
        } catch (error: any) {
          set({ error: error.message, loading: false });
          throw error;
        }
      },

      fetchLearningPlanGoals: async (id: number) => {
        set({ loading: true, error: null });
        try {
          const response = await getLearningPlanGoals(id);
          if (!response) {
            throw new Error("Failed to fetch goals");
          }
          set({ goals: response.data, loading: false });
        } catch (error: any) {
          set({ error: error.message, loading: false });
        }
      },

      addGoalToCalendar: async ({
        planId,
        credentials,
      }: {
        planId: number;
        credentials: AddGoalProps;
      }) => {
        set({ loading: true, error: null });
        try {
          const response = await addGoalToCalendar({
            planId,
            credentials,
          });

          set((state) => ({
            goals: state.goals
              ? [...state.goals, response.data]
              : [response.data],
            loading: false,
          }));
          return response;
        } catch (error: any) {
          set({ error: error.message, loading: false });
          throw error;
        }
      },

      removeGoal: async (planId: number, id: number) => {
        set({ loading: true, error: null });
        try {
          removeGoalFromCaledar({ planId, id });

          set((state) => ({
            goals: state.goals.filter((goal) => goal.id !== id),
            loading: false,
          }));
        } catch (error: any) {
          set({ error: error.message, loading: false });
          throw error;
        }
      },

      editGoal: async ({
        planId,
        credentials,
      }: {
        planId: number;
        credentials: EditGoalProps;
      }) => {
        set({ loading: true, error: null });
        try {
          const response = await editCalendarGoal({
            planId,
            credentials,
          });

          set((state) => ({
            goals: state.goals.map((goal) =>
              goal.id === credentials.id ? response.data : goal
            ),
            loading: false,
          }));
          return response;
        } catch (error: any) {
          set({ error: error.message, loading: false });
          throw error;
        }
      },

      addTask: async ({
        planId,
        goalId,
        credentials,
      }: {
        planId: number;
        goalId: number;
        credentials: GoalTimestamp;
      }) => {
        set({ loading: true, error: null });
        try {
          const response = await addTaskToGoal({
            planId,
            goalId,
            credentials,
          });

          set((state) => {
            const updatedGoals = state.goals?.map((goal) => {
              if (goal.id === goalId) {
                return {
                  ...goal,
                  tasks: goal.tasks
                    ? [...goal.tasks, response.data]
                    : [response.data],
                };
              }
              return goal;
            });

            return {
              goals: updatedGoals,
              loading: false,
            };
          });
          return response;
        } catch (error: any) {
          set({ error: error.message, loading: false });
          throw error;
        }
      },

      editTask: async ({
        planId,
        goalId,
        taskId,
        credentials,
      }: {
        planId: number;
        goalId: number;
        taskId: number;
        credentials: GoalTimestamp;
      }) => {
        set({ loading: true, error: null });
        try {
          const response = await editTaskInGoal({
            planId,
            goalId,
            taskId,
            credentials,
          });

          set((state) => {
            const updatedGoals = state.goals?.map((goal) => {
              if (goal.id === goalId) {
                return {
                  ...goal,
                  tasks: goal.tasks?.map((task) =>
                    task.id === taskId ? response.data : task
                  ),
                };
              }
              return goal;
            });

            return {
              goals: updatedGoals,
              loading: false,
            };
          });

          return response;
        } catch (error: any) {
          set({ error: error.message, loading: false });
          throw error;
        }
      },

      deleteTask: async ({
        planId,
        goalId,
        taskId,
      }: {
        planId: number;
        goalId: number;
        taskId: number;
      }) => {
        set({ loading: true, error: null });
        try {
          deleteTaskInGoal({ planId, goalId, taskId });

          set((state) => {
            const updatedGoals = state.goals?.map((goal) => {
              if (goal.id === goalId) {
                return {
                  ...goal,
                  tasks: goal.tasks?.filter((task) => task.id !== taskId),
                };
              }
              return goal;
            });

            return {
              goals: updatedGoals,
              loading: false,
            };
          });
          return true;
        } catch (error: any) {
          set({ error: error.message, loading: false });
          throw error;
        }
      },

      setCurrentLearningPlanId: (id: number) => {
        set({
          currentPlanId: id,
        });
      },

      resetDevelopmentState: () => {
        set({
          goals: [],
          learningPlan: [],
          currentPlanId: null,
        });
      },
    }),
    { name: "developmentStore" }
  )
);

export default useDevelopmentStore;
